import { isNil, isArray, isObject, isPlainObject, difference } from 'lodash'
import { CountryKoodistoCode } from 'todistukset-common/src/vtj/vtj-koodisto.util'
import { PlaceOfMarriage } from 'todistukset-common/src/vtj/types/place-of-marriage'
import {
  availableAETTodistusLanguages,
  availableElomakeTodistusLanguages,
  availableUAETTodistusLanguages,
  availableVtjOteTodistusLanguages,
} from 'todistukset-common/src/vtj/language.util'
import { MaritalStatus } from 'todistukset-common/src/vtj/MaritalStatus.enum'
import { TodistusKaannosKieli } from 'todistukset-common/src/vtj/types/todistus-kaannos-kieli.type'
import { TodistusPurpose } from 'todistukset-common/src/vtj/TodistusPurpose.enum'

export type EditableField<A> = {
  /**
   * VTJ-tietokannasta haettu arvo.
   *
   * @default null
   */
  vtjArvo: A | null

  /**
   * Hakijan tai ulkopuolisen järjestelmän ilmoittama arvo vireilletulo kanavasta.
   *
   * @default null
   */
  ilmoitettuArvo: A | null

  /**
   * Virkailijan syöttämä arvo. Lähtökohtaisesti hakijan on
   * todistettava arvo ennen kuin virkailja voi arvoa syöttää.
   *
   * @default null
   */
  virkailijaArvo: A | null
}

export const aetAllowedVihkivaltios = [
  PlaceOfMarriage.FINLAND,
  PlaceOfMarriage.NORDICS,
] as const

/**
 * Muokattavissa oleva Asuinvaltio tieto joka tulee näkyville todistukselle.
 */
export type Asuinvaltio = EditableField<CountryKoodistoCode>

/**
 * Muokattavissa oleva AvioliittojenLukumaara tieto joka tulee näkyville todistukselle.
 */
export type AvioliittojenLukumaara = EditableField<number>

/**
 * Muokattavissa olevat henkilön yhteystiedot.
 *
 * vtjArvo can never be other than null because value does not come from VTJ.
 */
export type Puhelinnumero = EditableField<string> & { vtjArvo: null }
export type Sahkopostiosoite = EditableField<string> & { vtjArvo: null }

/**
 * Muokattavissa oleva UskonnollinenYhdyskunta tieto joka tulee näkyville todistukselle.
 */
export type UskonnollinenYhdyskunta = EditableField<string>

/**
 * Muokattavissa oleva kansalaisuus tieto joka tulee näkyville todistukselle.
 */
export type Kansalaisuus = EditableField<CountryKoodistoCode[]>

/**
 * Muokattavissa oleva siviilisääty tieto joka tulee näkyville todistukselle.
 */
export type Siviilisaaty = EditableField<MaritalStatus>

/**
 * Muokattavissa oleva etunimi tieto joka tulee näkyville todistukselle.
 */
export type Etunimet = EditableField<string>

/**
 * Muokattavissa oleva sukunimi tieto joka tulee näkyville todistukselle.
 */
export type Sukunimi = EditableField<string>

/**
 * Muokattavissa oleva UusiSukunimi tieto joka tulee näkyville todistukselle.
 *
 * vtjArvo can never be other than null because value does not come from VTJ.
 */
export type UusiSukunimi = EditableField<string> & { vtjArvo: null }

/**
 * Muokattavissa oleva täppä joka määrää että säilyykö henkilön sukunimi vihkimisen jälkeen samana.
 *
 * vtjArvo can never be other than null because value does not come from VTJ.
 */
export type SailyykoSukunimi = EditableField<boolean> & { vtjArvo: null }

/**
 * Muokattavissa oleva täppä joka kertoo onko kirkollisten tietojen merkitsemistä todistukselle pyydetty
 *
 * vtjArvo can never be other than null because value does not come from VTJ.
 */
export type KirkollinenVihkiminen = EditableField<boolean> & {
  vtjArvo: null
}

/**
 * Muokattavissa oleva täppä joka kertoo onko puoliso vakuuttanut, että ei ole sukua toiselle puolisolle.
 *
 * vtjArvo can never be other than null because value does not come from VTJ.
 */
export type SukulaisuussuhdeVakuutettu = EditableField<boolean> & {
  vtjArvo: null
}

/**
 * Muokattavissa oleva todistuksen kieli.
 *
 * vtjArvo can never be other than null because value does not come from VTJ.
 */
export type TodistusKieli = EditableField<
  (typeof availableVtjOteTodistusLanguages)[number]
> & { vtjArvo: null }
export type AetTodistusKieli = EditableField<
  (typeof availableAETTodistusLanguages)[number]
> & { vtjArvo: null }
export type UaetTodistusKieli = EditableField<
  (typeof availableUAETTodistusLanguages)[number]
> & { vtjArvo: null }
export type ElomakeTodistusKieli = EditableField<
  (typeof availableElomakeTodistusLanguages)[number]
> & { vtjArvo: null }
export type TodistuspyyntoTodistusKieli =
  | TodistusKieli
  | AetTodistusKieli
  | UaetTodistusKieli
  | ElomakeTodistusKieli

/**
 * Muokattavissa oleva käännöstodistuksen kieli.
 *
 * vtjArvo can never be other than null because value does not come from VTJ.
 */
export type KaannosKieli = EditableField<TodistusKaannosKieli> & {
  vtjArvo: null
}

/**
 * Muokattavissa oleva syntymäpäiva, format YYYY-MM-DD.
 */
export type Syntymapaiva = EditableField<string>

/**
 * Muokattavissa oleva vihkivaltio tieto joka tulee näkyviin todistukselle.
 *
 * vtjArvo can never be other than null because value does not come from VTJ.
 */
export type Vihkivaltio = EditableField<
  (typeof aetAllowedVihkivaltios)[number]
> & {
  vtjArvo: null
}

export type VihkivaltioUAET = EditableField<CountryKoodistoCode> & {
  vtjArvo: null
}

/**
 * Muokattavissa oleva täppä joka määrää että onko tilattu käännös todistuksesta.
 *
 * vtjArvo can never be other than null because value does not come from VTJ.
 */
export type IncludeKaannos = EditableField<boolean> & { vtjArvo: null }

/**
 * Muokattavissa oleva täppä joka määrää että onko tilattu apostille todistus.
 *
 * vtjArvo can never be other than null because value does not come from VTJ.
 */
export type IncludeApostille = EditableField<boolean> & { vtjArvo: null }

/**
 * Lisätäänkö todistukseen entiset vakinaiset osoitteet.
 *
 * vtjArvo can never be other than null because value does not come from VTJ.
 */
export type IncludePreviousPermanentAddresses = EditableField<boolean> & {
  vtjArvo: null
}

/**
 * Kuinka monen vuoden ajalta todistukseen lisätään entiset osoitteet.
 *
 * vtjArvo can never be other than null because value does not come from VTJ.
 */
export type PreviousAddressesOverYears = EditableField<number> & {
  vtjArvo: null
}

/**
 * Muokattavissa oleva Syntymapaikka tieto joka tulee näkyville todistukselle.
 */
export type Syntymapaikka = EditableField<string>

/**
 * Muokattavissa oleva tilausten määrä, joka kertoo kuinka monta kappaletta todistuksia henkilöstä on tilattu.
 */
export type TilaustenMaara = EditableField<number> & { vtjArvo: null }

/**
 * Muokattavissa oleva Syntymavaltio tieto joka tulee näkyville todistukselle.
 */
export type Syntymavaltio = EditableField<CountryKoodistoCode>

/**
 * Lisätäänkö todistukseen siviilisääty.
 *
 * vtjArvo can never be other than null because value does not come from VTJ.
 */
export type IncludeMaritalStatus = EditableField<boolean> & {
  vtjArvo: null
}

/**
 * Lisätäänkö todistukseen suomalainen henkilötunnus.
 *
 * vtjArvo can never be other than null because value does not come from VTJ.
 */
export type IncludeHetu = EditableField<boolean> & {
  vtjArvo: null
}

/**
 * Lisätäänkö todistukseen entiset nimet.
 *
 * vtjArvo can never be other than null because value does not come from VTJ.
 */
export type IncludePreviousNames = EditableField<boolean> & {
  vtjArvo: null
}

/**
 * Lisätäänkö todistukseen puolison entiset nimet.
 *
 * vtjArvo can never be other than null because value does not come from VTJ.
 */
export type IncludeSpousePreviousNames = EditableField<boolean> & {
  vtjArvo: null
}

/**
 * Lisätäänkö todistukseen entiset avioliitot.
 *
 * vtjArvo can never be other than null because value does not come from VTJ.
 */
export type IncludePreviousMarriages = EditableField<boolean> & {
  vtjArvo: null
}

/**
 * Lisätäänkö todistukseen kansalaisuus.
 *
 * vtjArvo can never be other than null because value does not come from VTJ.
 */
export type IncludeNationality = EditableField<boolean> & {
  vtjArvo: null
}

/**
 * Lisätäänkö todistukseen vakinainen osoite.
 *
 * vtjArvo can never be other than null because value does not come from VTJ.
 */
export type IncludePermanentAddress = EditableField<boolean> & {
  vtjArvo: null
}

/**
 * Lisätäänkö todistukseen entiset tilapäiset osoitteet.
 *
 * vtjArvo can never be other than null because value does not come from VTJ.
 */
export type IncludePreviousTemporaryAddresses = EditableField<boolean> & {
  vtjArvo: null
}

/**
 * Lisätäänkö todistukseen entiset kotikunnat.
 *
 * vtjArvo can never be other than null because value does not come from VTJ.
 */
export type IncludePreviousKotikunta = EditableField<boolean> & {
  vtjArvo: null
}

/**
 * Lisätäänkö todistukseen kotikunta.
 *
 * vtjArvo can never be other than null because value does not come from VTJ.
 */
export type IncludeKotikunta = EditableField<boolean> & {
  vtjArvo: null
}

/**
 * Lisätäänkö todistukseen vanhempien hetut.
 *
 * vtjArvo can never be other than null because value does not come from VTJ.
 */
export type IncludeParentsHetu = EditableField<boolean> & {
  vtjArvo: null
}

/**
 * Lisätäänkö todistukseen vanhempien entiset sukunimet.
 *
 * vtjArvo can never be other than null because value does not come from VTJ.
 */
export type IncludeParentsPreviousNames = EditableField<boolean> & {
  vtjArvo: null
}

/**
 * Lisätäänkö todistukseen avioliiton järjestysnumero.
 *
 * vtjArvo can never be other than null because value does not come from VTJ.
 */
export type IncludeNumberOfMarriages = EditableField<boolean> & {
  vtjArvo: null
}

/**
 * Muokattavissa oleva käyttöalue, joka kertoo missä maassa todistusta aiotaan käyttää.
 *
 * vtjArvo can never be other than null because value does not come from VTJ.
 */
export type Kayttoalue = EditableField<string> & { vtjArvo: null }

/**
 * Muokattavissa oleva käyttötarkoitus, joka kuvaa mihin tarkoitukseen todistusta haetaan.
 *
 * vtjArvo can never be other than null because value does not come from VTJ.
 */
export type Kayttotarkoitus = EditableField<TodistusPurpose> & { vtjArvo: null }

/**
 * Muokattavissa oleva käyttötarkoituksen kuvaus. Tarkentaa Kayttotarkoitus tietoa. Vaapateksti.
 *
 * vtjArvo can never be other than null because value does not come from VTJ.
 */
export type KayttotarkoitusKuvailu = EditableField<string> & { vtjArvo: null }

export const finalEditableFieldValue = <T>(
  editableField: EditableField<T> | null
): T | null => {
  return (
    editableField?.virkailijaArvo ??
    editableField?.ilmoitettuArvo ??
    editableField?.vtjArvo ??
    null
  )
}

/**
 * Use this function to get a final value if it is certain that the final value of an EditableField can't be null.
 * Note, throws on failure.
 */
export const finalNonNullEditableFieldValue = <T>(
  editableField: EditableField<T>
): T => {
  const value = finalEditableFieldValue(editableField)
  if (value === null) {
    throw new Error('final EditableField value is null')
  }
  return value
}

// The generic 'finalEditableFieldValue' does not work well for UusiSukunimi, because its value depends on
// SailyykoSukunimi. For instance, in a case where:
// UusiSukunimi, ilmoitettu:      Heinänen
// SailyykoSukunimi, ilmoitettu:  false
// UusiSukunimi, virkailija:      NULL
// SailyykoSukunimi, virkalilija: true
//
// Final value for UusiSukunimi is NULL, not Heinänen (generic 'finalEditableFieldValue' would return it).
//
export const finalUusiSukunimiValue = (
  newLastName: UusiSukunimi | null,
  keepingCurrentLastName: SailyykoSukunimi | null
): string | null => {
  if (keepingCurrentLastName?.virkailijaArvo === false) {
    return newLastName?.virkailijaArvo ?? null
  } else if (keepingCurrentLastName?.virkailijaArvo === true) {
    return null
  }
  return newLastName?.ilmoitettuArvo ?? null
}

export const emptyEditableField = <T extends EditableField<unknown>>(): T =>
  ({
    vtjArvo: null,
    ilmoitettuArvo: null,
    virkailijaArvo: null,
  } as T)

export const editVtjArvo = <T extends EditableField<unknown>>(
  editableField: T | null,
  value: T['vtjArvo'] | null
): T => {
  return {
    ...emptyEditableField(),
    ...editableField,
    vtjArvo: value,
  }
}

export const editIlmoitettuArvo = <T extends EditableField<unknown>>(
  editableField: T | null,
  value: T['ilmoitettuArvo'] | null
): T => {
  return {
    ...emptyEditableField(),
    ...editableField,
    ilmoitettuArvo: value,
  }
}

export const editVirkailijaArvo = <T extends EditableField<unknown>>(
  editableField: T | null,
  value: T['virkailijaArvo'] | null
): T => {
  return {
    ...emptyEditableField(),
    ...editableField,
    virkailijaArvo: value,
  }
}

export const hasEditableFieldValue = <T extends EditableField<unknown>>(
  editableField: T | null
): boolean =>
  editableField !== null &&
  (!isNil(editableField.vtjArvo) ||
    !isNil(editableField.ilmoitettuArvo) ||
    !isNil(editableField.virkailijaArvo))

// eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types, @typescript-eslint/no-explicit-any
export const recursivelyResetIlmoitettuArvo = (object: any): any => {
  if (isArray(object)) {
    return object.map((o: unknown) => recursivelyResetIlmoitettuArvo(o))
  }
  if (isPlainObject(object)) {
    if (isEditableField(object)) {
      return editIlmoitettuArvo(object, null)
    } else {
      return Object.fromEntries(
        Object.entries(object).map(([key, value]) => {
          if (isArray(value) || isObject(value)) {
            return [key, recursivelyResetIlmoitettuArvo(value)]
          }
          return [key, value]
        })
      )
    }
  }
  return object
}

type Prettify<T> = {
  [K in keyof T]: T[K] extends Date | unknown ? T[K] : Prettify<T[K]>
} & unknown
export type FlattenedEditableField<Field> = Field extends EditableField<infer T>
  ? T
  : never
export type FlattenedEditableForm<Form> = Prettify<
  Form extends EditableField<unknown>
    ? FlattenedEditableField<Form>
    : {
        [Prop in keyof Form]: Form[Prop] extends EditableField<unknown> | null
          ? FlattenedEditableField<Form[Prop]> | null
          : Form[Prop] extends object
          ? FlattenedEditableForm<Form[Prop]>
          : Form[Prop]
      }
>

export const recursivelyGetFinalValue = <Form>(
  object: Form
): FlattenedEditableForm<Form> => {
  if (isArray(object)) {
    return object.map((o: unknown) =>
      recursivelyGetFinalValue(o)
    ) as FlattenedEditableForm<Form>
  }
  if (isPlainObject(object)) {
    if (isEditableField(object)) {
      return finalEditableFieldValue(object) as FlattenedEditableForm<Form>
    } else {
      return Object.fromEntries(
        Object.entries(object as object).map(([key, value]) => {
          if (isArray(value) || isObject(value)) {
            return [key, recursivelyGetFinalValue(value)]
          }
          return [key, value]
        })
      ) as FlattenedEditableForm<Form>
    }
  }
  return object as FlattenedEditableForm<Form>
}

export const isEditableField = (
  value: unknown
): value is EditableField<unknown> => {
  if (
    isPlainObject(value) &&
    difference(Object.keys(value as object), [
      'vtjArvo',
      'ilmoitettuArvo',
      'virkailijaArvo',
    ]).length === 0
  ) {
    return true
  }

  return false
}

export const isEditableFieldBoolean = (
  value: unknown
): value is EditableField<boolean> => {
  return (
    isEditableField(value) &&
    typeof finalEditableFieldValue(value) === 'boolean'
  )
}

export const isEditableFieldArray = (
  value: unknown
): value is Array<EditableField<unknown>> => {
  if (Array.isArray(value) && isEditableField(value[0])) {
    return true
  }

  return false
}

export const mapEditableField = <T extends EditableField<unknown>, R>(
  editableField: T | null,
  fn: (
    a: T['vtjArvo'] | T['ilmoitettuArvo'] | T['virkailijaArvo'] | null
  ) => R | null
): EditableField<R> => {
  if (editableField === null) {
    return {
      virkailijaArvo: fn(null),
      ilmoitettuArvo: fn(null),
      vtjArvo: fn(null),
    }
  } else {
    return {
      virkailijaArvo: fn(editableField.virkailijaArvo),
      ilmoitettuArvo: fn(editableField.ilmoitettuArvo),
      vtjArvo: fn(editableField.vtjArvo),
    }
  }
}

export const validateVtjArvoNull = <T extends EditableField<unknown>>(
  editabledField: T
): T & { vtjArvo: null } => {
  if (editabledField.vtjArvo !== null) {
    throw new Error('Expected vtjArvo to be null')
  }
  return {
    ...editabledField,
    vtjArvo: null,
  }
}
